import * as React from 'react'
import CloudinaryImage from 'web-makeup/dist/CloudinaryImage'
import Skeleton from '@yisheng90/react-loading'
import { isAdmin, isProducer, isManager } from 'web-fns/dist/auth'
import { colors } from 'web-makeup/dist/design-system/config/data/theme'
import { Route } from 'react-router-dom'
import { useFlags } from 'launchdarkly-react-client-sdk'

import DefaultButton from '../../components/DefaultButton/DefaultButton'
import EnvironmentContext from '../../../EnvironmentContext'

import { Brand as BrandType } from '../../../../typings/types'

import {
  Container,
  Logo,
  Left,
  Right,
  H2,
  Menu,
  Centered,
  OrderTrigger,
  MobileMenu,
  ButtonWrapper,
  DefaultLogo,
  UploadLogoButton,
} from './DashboardHeader.style'

export interface DashboardHeaderProps {
  activeButton?: number
  brandProfileData?: BrandType
  brandId?: string
}
const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  activeButton,
  brandProfileData,
  brandId,
}) => {
  const [isMenuActive, setIsMenuActive] = React.useState(false)
  const [menuTitle, setMenuTitle] = React.useState('Dashboard')
  const [menuIcon, setIcon] = React.useState('')
  const [isHubSpotVisible, setIsHubSpotVisible] = React.useState(false)

  const { GEN1_BASE_URL, PRODUCTIZE_URL, MATCHING_URL, DELIVERY_URL } =
    React.useContext(EnvironmentContext) || ''

  const flags = useFlags()
  const brandDashboardLinks = flags['brand-dashboard-links']
  const contentManagerLink = flags['content-link']
  const diliverLInkVisible = flags['brand-dashboard-deliver-link']
  const BDTeamPage = flags['brand-dashboard-team-members-page']
  const BDEnterprisePage = flags['brand-dashboard-enterprise-page']
  const BDPaymentsPage = flags['brand-dashboard-payments-page']

  const isHidden = !(isAdmin() || isProducer() || isManager())

  const dashboardHeaderItems = [
    {
      label: 'Dashboard',
      iconPassive: 'v1688987759/-design-system/icon-dashboard-default.svg',
      iconActive: 'v1691471635/-design-system/icon-dashboard-active.svg',
      buttonLink: `/brand-dashboard/${brandId}`,
    },
    {
      label: 'Productize',
      iconPassive: 'v1691471639/-design-system/icon-productize-default.svg',
      iconActive: 'v1691471639/-design-system/icon-productize-active.svg',
      platformButtonLink: `${PRODUCTIZE_URL}/dashboard`,
      hidden: isHidden,
    },
    {
      label: 'New Order',
      iconPassive: 'v1689746784/-design-system/icon-new-order-default.svg',
      iconActive: 'v1689746783/-design-system/icon-new-order-active.svg',
      buttonLink: `/brand-dashboard/orders/${brandId}`,
    },
    {
      label: 'Match',
      iconPassive: 'v1703006203/-design-system/icon-matching-default.svg',
      iconActive: 'v1703006203/-design-system/icon-matching-active.svg',
      platformButtonLink: `${MATCHING_URL}/matching`,
      hidden: isHidden,
    },
    {
      label: 'Deliver',
      iconPassive: 'v1691471635/-design-system/icon-delivery-default.svg',
      iconActive: 'v1691471635/-design-system/icon-delivery-active.svg',
      platformButtonLink: `${DELIVERY_URL}`,
      hidden: !diliverLInkVisible || isHidden,
    },
    {
      label: 'Wallets',
      iconPassive: 'v1689746783/-design-system/icon-wallets-default.svg',
      iconActive: 'v1689746783/-design-system/icon-wallets-active.svg',
      buttonLink: `/brand-dashboard/wallets/${brandId}`,
    },
    {
      label: 'Content',
      iconPassive: 'v1689746784/-design-system/icon-content-default.svg',
      iconActive: 'v1689746785/-design-system/icon-content-active.svg',
      platformButtonLink: `https://contentmanager.90seconds.com/dashboard`,
      hidden: contentManagerLink,
    },
    {
      label: 'Settings',
      iconPassive: 'v1688980256/-design-system/icon-settings-default.svg',
      iconActive: 'v1688980256/-design-system/icon-settings-active.svg',
      buttonLink: `/brand-dashboard/settings/${brandId}`,
    },
    {
      label: 'Team',
      iconPassive: 'v1688980073/-design-system/icon-team-default.svg',
      iconActive: 'v1688980075/-design-system/icon-team-active.svg',
      buttonLink: `/brand-dashboard/team/${brandId}`,
      platformButtonLink: `${GEN1_BASE_URL}/organisations/${brandId}/team`,
      flagEnabled: BDTeamPage,
    },
    // {
    //   label: 'Integrations',
    //   iconPassive: 'v1688980071/-design-system/icon-integrations-default.svg',
    //   iconActive: 'v1688980071/-design-system/icon-integrations-active.svg',
    //   buttonLink: `/brand-dashboard/integrations/${brandId}`,
    //   platformButtonLink: `${GEN1_BASE_URL}/organisations/${brandId}/connections`,
    // },
    {
      label: 'Billing',
      iconPassive: 'v1688980074/-design-system/icon-billing-default.svg',
      iconActive: 'v1688980075/-design-system/icon-billing-active.svg',
      buttonLink: `/brand-dashboard/payments/${brandId}`,
      platformButtonLink: `${GEN1_BASE_URL}/organisations/${brandId}/account`,
      flagEnabled: BDPaymentsPage,
    },
    {
      label: 'Our Enterprise',
      iconPassive: 'v1688980073/-design-system/icon-enterprise-default.svg',
      iconActive: 'v1688980073/-design-system/icon-enterprise-active.svg',
      buttonLink: `/brand-dashboard/sub-brands/${brandId}`,
      platformButtonLink: `${GEN1_BASE_URL}/organisations/${brandId}/subbrands`,
      flagEnabled: BDEnterprisePage,
    },
    {
      label: 'Support',
      iconPassive: 'v1688980075/-design-system/icon-support-default.svg',
      iconActive: 'v1688980076/-design-system/icon-support-active.svg',
      supportLink: true,
    },
  ]
  const isMounted = React.useRef(true)

  const orderTriggerReff = React.useRef<HTMLDivElement>(null)

  const isSubBrandView = localStorage.getItem('isSubBrand')
  const parentBrandId = localStorage.getItem('parentId')

  const openHubSpot = () =>
    window && window.HubSpotConversations && window.HubSpotConversations.widget.open()
  const closeHubSpot = () =>
    window && window.HubSpotConversations && window.HubSpotConversations.widget.close()

  const addClassNameListener = (elemId, callback) => {
    const elem = elemId
    const lastClassName = elem && elem.className
    window.setInterval(() => {
      const className = elem && elem.className
      if (className !== lastClassName) {
        callback()
      }
    }, 10)
  }

  const isOrderPage = document.body.classList.contains('new-order')

  addClassNameListener(document.getElementsByTagName('html')[0], () => {
    if (document.getElementsByTagName('html')[0].className === 'hs-messages-widget-open') {
      setIsHubSpotVisible(true)
    } else {
      setIsHubSpotVisible(false)
    }
  })

  React.useEffect(
    () => {
      const observer = new MutationObserver(() => {
        if (isMounted.current) {
          setMenuTitle(document.body.className)
        }

        const currentpageItem = dashboardHeaderItems.filter(
          item =>
            item.label.toUpperCase() === document.body.className.replace(/-/g, ' ').toUpperCase()
        )
        if (currentpageItem[0]) {
          if (isMounted.current) {
            setIcon(currentpageItem[0].iconActive)
          }
        }
      })

      observer.observe(document.body, {
        attributes: true,
        attributeFilter: ['class'],
        childList: false,
        characterData: false,
      })
    },
    [document.body]
  )

  React.useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  const outsideClick = ref => {
    React.useEffect(
      () => {
        const handleClickOutside = event => {
          if (ref.current && !ref.current.contains(event.target)) {
            setIsMenuActive(false)
          }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => {
          document.removeEventListener('mousedown', handleClickOutside)
        }
      },
      [ref]
    )
  }
  outsideClick(orderTriggerReff)

  return (
    <>
      {isSubBrandView && (
        <ButtonWrapper isAdmin={isAdmin()} isSubBrandView={isSubBrandView}>
          <DefaultButton
            fontSize="0.875rem"
            padding="0 2.5rem"
            buttonHeight="2.75rem"
            backgroundColor={colors.nebula}
            hoverBackground={colors.dawn}
            color={colors.white}
            hoverColor={colors.white}
            onClick={() => {
              if (window.env) {
                window.location.href = `${
                  window.env.BRAND_BASE_URL
                }/brand-dashboard/${parentBrandId}`
              }
              localStorage.removeItem('isSubBrand')
              localStorage.removeItem('parentId')
            }}
          >
            Back to parent brand
          </DefaultButton>
        </ButtonWrapper>
      )}
      <OrderTrigger
        ref={orderTriggerReff}
        isAdmin={isAdmin()}
        onClick={() => setIsMenuActive(!isMenuActive)}
        isActive={isMenuActive}
        isSubBrandView={isSubBrandView}
      >
        <CloudinaryImage
          useSourceFormat
          source={{
            id: menuIcon,
            type: 'upload',
          }}
        />
        {menuTitle}
        {isMenuActive && (
          <MobileMenu>
            {dashboardHeaderItems &&
              dashboardHeaderItems.map((item, index) => {
                if (item.hidden) {
                  return
                }
                return !brandDashboardLinks || item.flagEnabled === true ? (
                  <li
                    key={index}
                    className={
                      index === (isOrderPage ? 2 : activeButton ? activeButton + 3 : 0)
                        ? 'active'
                        : ''
                    }
                  >
                    <CloudinaryImage
                      useSourceFormat
                      source={{
                        id:
                          index === (isOrderPage ? 2 : activeButton ? activeButton + 3 : 0)
                            ? item.iconActive
                            : item.iconPassive,
                        type: 'upload',
                      }}
                    />
                    <a
                      key={index}
                      onClick={e => {
                        if (item.supportLink) {
                          e.preventDefault()
                          openHubSpot()
                        }
                      }}
                      href={
                        item.buttonLink || item.platformButtonLink || `/brand-dashboard/${brandId}`
                      }
                    >
                      {item.label}
                    </a>
                  </li>
                ) : (
                  <li
                    key={index}
                    className={
                      index === (isOrderPage ? 2 : activeButton ? activeButton + 3 : 0)
                        ? 'active'
                        : ''
                    }
                  >
                    <CloudinaryImage
                      useSourceFormat
                      source={{
                        id:
                          index === (isOrderPage ? 2 : activeButton ? activeButton + 3 : 0)
                            ? item.iconActive
                            : item.iconPassive,
                        type: 'upload',
                      }}
                    />
                    <a
                      onClick={e => {
                        if (item.supportLink) {
                          e.preventDefault()
                          openHubSpot()
                        }
                      }}
                      href={item.platformButtonLink ? item.platformButtonLink : item.buttonLink}
                    >
                      {item.label}
                    </a>
                  </li>
                )
              })}
          </MobileMenu>
        )}
      </OrderTrigger>
      <Container isAdmin={isAdmin()}>
        <Left data-testid="left-header">
          {isSubBrandView && (
            <ButtonWrapper>
              <DefaultButton
                fontSize="0.875rem"
                padding="0 2.5rem"
                buttonHeight="2.75rem"
                backgroundColor={colors.nebula}
                hoverBackground={colors.dawn}
                color={colors.white}
                hoverColor={colors.white}
                onClick={() => {
                  if (window.env) {
                    window.location.href = `${
                      window.env.BRAND_BASE_URL
                    }/brand-dashboard/${parentBrandId}`
                  }
                  localStorage.removeItem('isSubBrand')
                  localStorage.removeItem('parentId')
                }}
              >
                Back to parent brand
              </DefaultButton>
            </ButtonWrapper>
          )}
          <Logo data-testid="logo">
            {brandProfileData && brandProfileData.avatar && brandProfileData.avatar.cloudinaryId ? (
              brandProfileData.avatar.cloudinaryId === 'placeholders/icon-brand' ? (
                <DefaultLogo>{brandProfileData.tradingName.slice(0, 1)}</DefaultLogo>
              ) : (
                <CloudinaryImage
                  useSourceFormat
                  source={{
                    id: brandProfileData.avatar.cloudinaryId,
                    type: 'fetch',
                  }}
                />
              )
            ) : (
              <Skeleton width={94} height={94} circle />
            )}
          </Logo>
          <div>
            <H2
              data-testid="brand-name"
              data-tip={(brandProfileData && brandProfileData.tradingName) || ''}
            >
              {brandProfileData && brandProfileData.tradingName ? (
                brandProfileData.tradingName
              ) : (
                <Centered>
                  <Skeleton width={148} height={32} />
                </Centered>
              )}
            </H2>
          </div>
          {brandProfileData &&
            brandProfileData.avatar &&
            brandProfileData.avatar.cloudinaryId === 'placeholders/icon-brand' && (
              <Route
                render={({ history }) => (
                  <UploadLogoButton
                    onClick={() => {
                      brandDashboardLinks
                        ? (window.location.href = `${GEN1_BASE_URL}/organisations/${brandId}/details`)
                        : history.push(`/brand-dashboard/settings/${brandId}`)
                    }}
                  >
                    <img
                      src="https://res.cloudinary.com/ninety-seconds/image/upload/v1695270308/-design-system/upload-color.svg"
                      alt="Uplaod brand logo"
                    />{' '}
                    Upload brand logo
                  </UploadLogoButton>
                )}
              />
            )}
        </Left>
        <Right data-testid="right-header">
          <Menu>
            {dashboardHeaderItems &&
              dashboardHeaderItems.map((item, index) => {
                if (item.hidden) {
                  return
                }
                return !brandDashboardLinks || item.flagEnabled === true ? (
                  <li
                    key={index}
                    className={
                      index === (isOrderPage ? 2 : activeButton ? activeButton + 3 : 0)
                        ? 'active'
                        : ''
                    }
                  >
                    <Route
                      render={({ history }) => (
                        <div
                          key={index}
                          onClick={e => {
                            if (item.buttonLink) {
                              e.preventDefault()
                              history.push(item.buttonLink || `/brand-dashboard/${brandId}`)
                              closeHubSpot()
                            } else {
                              item.supportLink
                                ? openHubSpot()
                                : item.platformButtonLink &&
                                  (window.location.href = item.platformButtonLink)
                            }
                          }}
                        >
                          <CloudinaryImage
                            useSourceFormat
                            source={{
                              id:
                                index === (isOrderPage ? 2 : activeButton ? activeButton + 3 : 0) ||
                                (item.supportLink && isHubSpotVisible)
                                  ? item.iconActive
                                  : item.iconPassive,
                              type: 'upload',
                            }}
                          />
                          {item.label}
                        </div>
                      )}
                    />
                  </li>
                ) : (
                  <li
                    key={index}
                    className={
                      index === (isOrderPage ? 2 : activeButton ? activeButton + 3 : 0)
                        ? 'active'
                        : ''
                    }
                  >
                    <Route
                      render={({ history }) => (
                        <div
                          key={index}
                          onClick={e => {
                            if (item.platformButtonLink) {
                              item.platformButtonLink &&
                                (window.location.href = item.platformButtonLink)
                            } else {
                              e.preventDefault()
                              if (item.supportLink) {
                                openHubSpot()
                              } else {
                                history.push(item.buttonLink || `/brand-dashboard/${brandId}`)
                                closeHubSpot()
                              }
                            }
                          }}
                        >
                          <CloudinaryImage
                            useSourceFormat
                            source={{
                              id:
                                index === (isOrderPage ? 2 : activeButton ? activeButton + 3 : 0) ||
                                (item.supportLink && isHubSpotVisible)
                                  ? item.iconActive
                                  : item.iconPassive,
                              type: 'upload',
                            }}
                          />
                          {item.label}
                        </div>
                      )}
                    />
                  </li>
                )
              })}
          </Menu>
        </Right>
      </Container>
    </>
  )
}

export default React.memo(DashboardHeader)



// WEBPACK FOOTER //
// ./src/components/BrandDashboard/components/DashboardHeader/DashboardHeader.tsx